import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { ArrayHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ArrayHelper";
import { DateHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/DateHelper";
import { ObjectHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ObjectHelper";
import { StringHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/StringHelper";
import { IJNativeEvent } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { PropertyChange } from "@seriously-rad/seriouslyrad-framework-core/PropertyChange";
import { Property, PropertyObject } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { ApplicationInformationService } from "@seriously-rad/seriouslyrad-framework-core/RadWindow";
import { WebApiClientService } from "@seriously-rad/seriouslyrad-framework-core/Server/WebApiClientService";
import { ServiceManager } from "@seriously-rad/seriouslyrad-framework-core/ServiceManager";
import { CardList } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/CardList";
import { IInfiniteScrollPanel, InfiniteScrollPanel, InfiniteScrollPanelPropertyValues } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/InfiniteScrollPanel";
import { PageManager } from "@seriously-rad/seriouslyrad-framework-core/Ui/PageManager";
import { UserNotificationManagerService } from "../../UserNotificationManagerService";
import { UserNotification } from "./UserNotification";
import { NAMESPACE } from "./_Namespace";

export type UserNotificationListPropertyValues =
    InfiniteScrollPanelPropertyValues
    & {
        dialog: any;
    };

export type UserNotificationListProperties = {
    [name in keyof UserNotificationListPropertyValues]: Property<UserNotificationListPropertyValues[name]>
};

export interface IUserNotificationList extends IInfiniteScrollPanel, UserNotificationListProperties
{
}

export class UserNotificationList extends InfiniteScrollPanel implements IUserNotificationList
{
    // #region Static

    public static override classNamespace = NAMESPACE + "UserNotificationList";
    public static override className = "UserNotificationList";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<UserNotificationListPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                hasPreviousResults: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected cardList?: CardList;

    // #endregion

    // #region Properties

    public dialog!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<UserNotificationListPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public async loadDataAsync()
    {
        Assert.isNotNullOrUndefined(this.cardList, "this.cardList");

        this.cardList.controlsClear();
        await this._extendCardListAsync();
    }

    // #endregion

    // #region Protected Methods

    protected _dialogChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected async _extendCardListAsync()
    {
        Assert.isNotNullOrUndefined(this.cardList, "this.cardList");

        const
            lastCard = ArrayHelper.lastOrDefault(this.cardList.controlsGet()),
            dataServiceData = {
                lastReferenceNumber: lastCard ? lastCard.tag.get().referenceNumber : null,
                pageSize: 10
            },
            data = await (
                ServiceManager.getRequired(WebApiClientService)
                    .createApiMethodRequest(
                        "DataService",
                        "CallDataService",
                        {
                            id: "cc235279-29c2-4a76-b08a-71e574e614ee",
                            operation: "ListUserNotifications",
                            data: ObjectHelper.toJson(dataServiceData)
                        },
                        null,
                        false
                    )
                    .executeAsync()
            );

        if (!data)
            return;

        this.hasNextResults.set(data.hasMoreResults);

        data.results.forEach((item: any) =>
        {
            const
                createdDate = item.createdDate ? DateHelper.fromString(item.createdDate, "yyyy-MM-ddTHH:mm:ss.fffffffZ") : null;

            new UserNotification({
                tag: item,
                targetUrl: item.targetUrl,
                text: item.message,
                title: item.title,
                subTitle: createdDate ? DateHelper.toString(DateHelper.convertToUtc(createdDate), "dd/MM/yyyy HH:mm:ss") : null
            }).appendTo(this.cardList);
        });
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: UserNotificationListPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.dialog = new PropertyObject(this, propertyValues.dialog, this._dialogChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();
        this.on("loadMore", { self: this }, this.onLoadMore);
        this.on("userNotificationButtonClicked", { self: this }, this.onUserNotificationButtonClicked);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.cardList = new CardList().appendTo(this);

        return $$element;
    }

    // #endregion

    // #region Event Handlers

    protected onLoadMore(evt: IJNativeEvent, info: any)
    {
        const
            self = evt.data.self as this;

        if (info.location === "top" && !self.hasPreviousResults.get())
            return;

        if (info.location === "bottom" && !self.hasNextResults.get())
            return;

        self._extendCardListAsync()
            .then(() =>
            {
                if (info.callback)
                    info.callback();
            });
    }
    protected async onUserNotificationButtonClicked(evt: IJNativeEvent, eventData: any)
    {
        evt.stopPropagation();

        const
            buttonTag = eventData.button ? eventData.button.tag.get() : null,
            userNotificationTag = eventData.notification ? eventData.notification.tag.get() : null;

        if (!buttonTag || !userNotificationTag)
            return;

        await ServiceManager.getOptional(UserNotificationManagerService)?.dismissNotificationAsync(userNotificationTag.id);
        await eventData.notification.removeAsync();

        if (buttonTag !== "open")
            return;

        const
            targetUrl = eventData.notification.targetUrlGet();

        if (!targetUrl)
            return;

        await PageManager.instance?.navigateAsync(StringHelper.format("{0}{1}", ServiceManager.getRequired(ApplicationInformationService).virtualDirectory, targetUrl));
    }

    // #endregion

    // #endregion
}

